import { Injectable } from '@angular/core';
import { Http, Response, RequestOptions, Headers } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { AuthenticationService } from '../authentication/authentication.service';
import { UtilitiesService } from '../utilities/utilities.service';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { WarningService } from '../warning/warning.service';
import { maintenanceSite } from 'environments/properties';

export class HeaderKey {
  public static readonly contentType = 'Content-Type';
  public static readonly wemallAccessToken = 'X-WM-AccessToken';
  public static readonly wemallRefreshAccessToken = 'X-WM-RefreshToken';
  public static readonly actAccessToken = 'X-ACT-AccessToken';
  public static readonly actRefreshToken = 'X-ACT-RefreshToken';
  public static readonly authorization = 'Authorization';
}

export class HeaderValue {
  public static readonly applicationJson = 'application/json';
  public static readonly applicationWebForm = 'application/x-www-form-urlencoded';
}

export class HttpStatus {
  public static readonly notFound = 404;
  public static readonly serviceUnavailable = 503;
}

export enum HeaderTypes {
  AUTHORIZATION,
  AUTHORIZATION_WM_TOKEN,
  ACT_TOKEN,
  SSO_TOKEN,
  EXCLUDE_TOKEN,
  AUTHORIZATION_RANDOM_STR,
}

export enum ContentTypes {
  JSON,
  URLENCODE,
}

@Injectable()
export class ActHttpService {

  public static readonly unauthorizeMessage = 'Validate token fail.';

  constructor(
    private http: Http,
    private authenticationService: AuthenticationService,
    private utilitiesService: UtilitiesService,
    private appConfigService: AppConfigService,
    private warningService: WarningService
  ) { }

  rawGet(url: string, options?: RequestOptions): Observable<Response> {
    return this.http.get(url, options).catch(this.handleError);
  }

  rawPost(url: string, body: any, options?: RequestOptions): Observable<Response> {
    return this.http.post(url, body, options).catch(this.handleError);
  }

  rawPut(url: string, body: any, options?: RequestOptions): Observable<Response> {
    return this.http.put(url, body, options).catch(this.handleError);
  }

  rawDelete(url: string, options?: RequestOptions): Observable<Response> {
    return this.http.delete(url, options).catch(this.handleError);
  }

  get(url: string, options?: RequestOptions): Observable<Response> {
    return this.authenticationService.validateToken(this.authenticationService.getToken())
      .flatMap(result => {
        if (result) {
          options = this.updateTokenHeader(options);
          return this.rawGet(url, options);
        }
        this.handleUnauthorize();
        throw new Error(ActHttpService.unauthorizeMessage);
      });
  }

  post(url: string, body: any, options?: RequestOptions): Observable<Response> {
    return this.authenticationService.validateToken(this.authenticationService.getToken())
      .flatMap(result => {
        if (result) {
          options = this.updateTokenHeader(options);
          return this.rawPost(url, body, options);
        }
        this.handleUnauthorize();
        throw new Error(ActHttpService.unauthorizeMessage);
      });
  }

  put(url: string, body: any, options?: RequestOptions): Observable<Response> {
    return this.authenticationService.validateToken(this.authenticationService.getToken())
      .flatMap(result => {
        if (result) {
          options = this.updateTokenHeader(options);
          return this.rawPut(url, body, options);
        }
        this.handleUnauthorize();
        throw new Error(ActHttpService.unauthorizeMessage);
      });
  }

  delete(url: string, options?: RequestOptions): Observable<Response> {
    return this.authenticationService.validateToken(this.authenticationService.getToken())
      .flatMap(result => {
        if (result) {
          options = this.updateTokenHeader(options);
          return this.rawDelete(url, options);
        }
        this.handleUnauthorize();
        throw new Error(ActHttpService.unauthorizeMessage);
      });
  }

  getRequestOptions(headerType: HeaderTypes = HeaderTypes.ACT_TOKEN, contentType: ContentTypes = ContentTypes.JSON, email: string = '', payload?: any): RequestOptions {
    return this.authenticationService.getRequestOptions(headerType, contentType, email, payload);
  }

  updateTokenHeader(requestOption: RequestOptions): RequestOptions {
    if (!(requestOption && requestOption.headers)) {
      return requestOption;
    }

    const header = requestOption.headers;
    if (header.get(HeaderKey.actAccessToken)) {
      const actToken = this.authenticationService.getToken();
      header.set(HeaderKey.actAccessToken, actToken.accessToken.valueOf());
      header.set(HeaderKey.actRefreshToken, actToken.refreshToken.valueOf());
      return requestOption;
    }

    if (header.get(HeaderKey.wemallAccessToken)) {
      const actToken = this.authenticationService.getToken();
      header.set(HeaderKey.wemallAccessToken, actToken.accessToken.valueOf());
      header.set(HeaderKey.wemallRefreshAccessToken, actToken.refreshToken.valueOf());
      return requestOption;
    }

    return requestOption;
  }

  handleUnauthorize() {
    const userInfo = this.authenticationService.getUserInfo();
    this.authenticationService.clearToken();
    if (userInfo && userInfo.companyCode) {
      this.warningService.popDialogErrorRequest('popup-warning-session-expire', null, null, `/company/${userInfo.companyCode}`);
    } else {
      this.utilitiesService.redirectPage('/', this.appConfigService.getLanguage());
    }
  }

  handleError(error: Response | any) {
    if (error.status === HttpStatus.serviceUnavailable && error && error.json() && error.json().code === 'MAINTENANCE') {
      window.location.href = maintenanceSite;
    }
    return Observable.throw(error);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/act-http/act-http.service.ts